import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { BookmarkIcon, FolderIcon } from '@heroicons/react/24/outline';
import { bookmarkService } from '../services/bookmarkService';

const getInitials = (displayName) => {
  const name = (displayName || '').trim();
  if (!name) return '?';
  const parts = name.split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
};

const PublicProfile = () => {
  const { profileToken } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    bookmarkService.getPublicProfile(profileToken)
      .then(setProfile)
      .catch(err => setError(err.message || 'Profile not available.'))
      .finally(() => setLoading(false));
  }, [profileToken]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Minimal header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="container mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BookmarkIcon className="h-6 w-6 text-primary-600" />
            <span className="text-lg font-bold text-gray-900 dark:text-gray-100">BookMarker</span>
          </Link>
          <Link to="/login" className="btn-primary text-sm">Sign in</Link>
        </div>
      </header>

      <main className="container mx-auto px-4 py-10 max-w-2xl">
        {loading && (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-20">
            <FolderIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium text-gray-700 dark:text-gray-300">{error}</p>
            <Link to="/" className="mt-4 inline-block text-sm text-primary-600 dark:text-primary-400 hover:underline">
              Go to BookMarker
            </Link>
          </div>
        )}

        {!loading && profile && (
          <>
            {/* Profile header */}
            <div className="flex items-center gap-5 mb-8">
              <div
                className="flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold select-none"
                style={{ backgroundColor: profile.avatarColor || '#4f46e5' }}
              >
                {getInitials(profile.displayName)}
              </div>
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">
                  {profile.displayName || 'BookMarker User'}
                </h1>
                {profile.bio && (
                  <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">{profile.bio}</p>
                )}
              </div>
            </div>

            {/* Public collections */}
            <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Collections
              {profile.publicCollections.length > 0 && (
                <span className="ml-2 text-sm font-normal text-gray-400 dark:text-gray-500">
                  {profile.publicCollections.length}
                </span>
              )}
            </h2>

            {profile.publicCollections.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-6 py-10 text-center text-gray-400 dark:text-gray-500">
                <FolderIcon className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                <p>No public collections yet.</p>
              </div>
            ) : (
              <ul className="space-y-3">
                {profile.publicCollections.map(col => (
                  <li key={col.collectionId}>
                    <Link
                      to={`/shared/${col.shareToken}`}
                      className="flex items-start gap-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-5 py-4 hover:shadow-sm transition-shadow group"
                    >
                      <FolderIcon className="h-6 w-6 text-indigo-400 flex-shrink-0 mt-0.5 group-hover:text-indigo-600 transition-colors" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 dark:text-gray-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                          {col.name}
                        </p>
                        {col.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">{col.description}</p>
                        )}
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          {col.bookmarkCount === 1 ? '1 bookmark' : `${col.bookmarkCount} bookmarks`}
                        </p>
                      </div>
                      <span className="text-gray-300 group-hover:text-primary-400 transition-colors text-lg leading-none mt-0.5">→</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            {/* Sign-up CTA */}
            <div className="mt-12 bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800 rounded-2xl px-6 py-8 text-center">
              <BookmarkIcon className="h-10 w-10 text-primary-500 mx-auto mb-3" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Organise your own bookmarks</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                BookMarker automatically extracts summaries, tags, and categories from every page you save.
              </p>
              <Link to="/login" className="btn-primary inline-block">
                Get started — it's free
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default PublicProfile;
